import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, getFriendlyError } from "@/lib/api";
import { Flame, Mail, Lock, Loader2, ShieldCheck } from "lucide-react";

const inputCls =
  "bg-[#0F172A] border-white/[0.15] focus:border-[#E11D48] focus-visible:ring-[#E11D48]/30 text-white placeholder:text-slate-500 rounded-xl";

// Step 1 of checkout: create (or verify) the Sparkd account before Stripe.
export function PreCheckoutAuthModal({ open, onOpenChange, tierLabel, onAuthed }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [existing, setExisting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/companion/auth/register", {
        email: email.trim().toLowerCase(),
        password,
      });
      onAuthed({ user_id: data.user_id, email: data.email });
    } catch (err) {
      const status = err.response?.status;
      if (status === 409 || status === 401) {
        setExisting(true);
        setError(getFriendlyError(err, "This email already has a Sparkd account. Enter your password to continue."));
      } else {
        setError(getFriendlyError(err, "Could not set up your account. Please try again."));
      }
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#1F242D] border border-white/[0.08] rounded-2xl sm:max-w-md text-white"
        data-testid="precheckout-auth-modal"
      >
        <DialogHeader>
          <div className="flex items-center justify-center w-12 h-12 rounded-2xl spark-gradient mb-2">
            <Flame className="w-6 h-6 text-white" />
          </div>
          <DialogTitle className="font-heading text-2xl font-bold text-white">
            Create your Sparkd account
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {tierLabel
              ? `Set up your account to claim ${tierLabel}. You'll sign in with these same details in the app.`
              : "Create your Sparkd account to claim your membership."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              data-testid="precheckout-email"
              className={`pl-10 ${inputCls}`}
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={existing ? "Enter your existing password" : "Create a password (min 8 chars)"}
              data-testid="precheckout-password"
              className={`pl-10 ${inputCls}`}
            />
          </div>

          {error && (
            <p className="text-sm text-[#F43F5E]" data-testid="precheckout-error">{error}</p>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="precheckout-submit"
            className="w-full rounded-xl py-6 text-base font-semibold spark-gradient text-white hover:shadow-[0_0_24px_rgba(225,29,72,0.4)] transition-all"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Setting up…</>
            ) : (
              <>Continue to secure checkout</>
            )}
          </Button>

          <p className="flex items-center justify-center gap-1.5 text-xs text-slate-500">
            <ShieldCheck className="w-3.5 h-3.5" /> Payments secured by Stripe
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
